import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import api from '../../services/api';
import { FiArrowLeft, FiCopy, FiCheck, FiClock, FiRefreshCw } from 'react-icons/fi';
import toast from 'react-hot-toast';

const ExpressPaymentPage = () => {
  const navigate = useNavigate();
  const { orderId } = useParams();
  const { user } = useAuth();
  
  const [loading, setLoading] = useState(true);
  const [order, setOrder] = useState(null);
  const [payment, setPayment] = useState(null);
  const [copied, setCopied] = useState(false);
  const [checkingPayment, setCheckingPayment] = useState(false);

  // Scroll para o topo quando a página carrega
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    if (!user) {
      navigate('/login');
      return;
    }
    loadOrder();
  }, [orderId, user]);

  const loadOrder = async () => {
    try {
      const response = await api.get(`/express/${orderId}`);
      setOrder(response.data);
      
      if (response.data.payment_status === 'paid') {
        toast.success('Pagamento já confirmado!');
        navigate(`/express/acompanhar/${orderId}`);
        return;
      }
      
      // Criar pagamento PIX
      await createPixPayment();
    } catch (error) {
      toast.error('Erro ao carregar pedido');
      navigate('/express');
    } finally {
      setLoading(false);
    }
  };

  const createPixPayment = async () => {
    try {
      const response = await api.post(`/payments/pix/express/${orderId}`);
      setPayment(response.data);
    } catch (error) {
      toast.error('Erro ao gerar pagamento PIX');
    }
  };

  const copyPixCode = () => {
    if (payment?.qr_code) {
      navigator.clipboard.writeText(payment.qr_code);
      setCopied(true);
      toast.success('Código PIX copiado!');
      setTimeout(() => setCopied(false), 3000);
    }
  };

  const checkPaymentStatus = async () => {
    setCheckingPayment(true);
    try {
      const response = await api.get(`/express/${orderId}`);
      if (response.data.payment_status === 'paid') {
        toast.success('Pagamento confirmado!');
        navigate(`/express/acompanhar/${orderId}`);
      } else {
        toast.info('Pagamento ainda não confirmado. Aguarde alguns instantes.');
      }
    } catch (error) {
      toast.error('Erro ao verificar pagamento');
    } finally {
      setCheckingPayment(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-red-600 border-t-transparent"></div>
      </div>
    );
  }

  const serviceName = order?.service_type === 'leva_traz' ? 'Leva e Traz' : 'Ponto a Ponto';

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white shadow-sm sticky top-0 z-50">
        <div className="max-w-4xl mx-auto px-4 py-4 flex items-center gap-4">
          <button 
            onClick={() => navigate('/express')}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <FiArrowLeft className="w-5 h-5" />
          </button>
          <div>
            <h1 className="font-bold text-gray-900">Pagamento PIX</h1>
            <p className="text-sm text-gray-500">{serviceName}</p>
          </div>
        </div>
      </header>

      <main className="max-w-md mx-auto px-4 py-8">
        {/* Valor */}
        <div className="bg-white rounded-xl p-6 shadow-sm text-center mb-6">
          <p className="text-gray-500 mb-2">Valor a pagar:</p>
          <p className="text-4xl font-bold text-gray-900">
            R$ {order?.total?.toFixed(2)}
          </p>
          <p className="text-sm text-gray-500 mt-2">
            {order?.return_trip ? 'Ida e volta' : 'Somente ida'}
          </p>
        </div>

        {/* QR Code */}
        {payment?.qr_code_base64 && (
          <div className="bg-white rounded-xl p-6 shadow-sm mb-6">
            <h2 className="font-semibold text-gray-900 text-center mb-4">
              Escaneie o QR Code
            </h2>
            <div className="flex justify-center mb-4">
              <img 
                src={`data:image/png;base64,${payment.qr_code_base64}`}
                alt="QR Code PIX"
                className="w-48 h-48"
              />
            </div>
            <p className="text-sm text-gray-500 text-center">
              Abra o app do seu banco e escaneie o código acima
            </p>
          </div>
        )}

        {/* Código PIX */}
        {payment?.qr_code && (
          <div className="bg-white rounded-xl p-6 shadow-sm mb-6">
            <h2 className="font-semibold text-gray-900 mb-3">
              Ou copie o código PIX:
            </h2>
            <div className="bg-gray-100 rounded-lg p-3 mb-3">
              <p className="text-xs text-gray-600 break-all font-mono">
                {payment.qr_code.substring(0, 50)}...
              </p>
            </div>
            <button
              onClick={copyPixCode}
              className={`w-full py-3 rounded-xl font-medium flex items-center justify-center gap-2 transition-colors ${
                copied 
                  ? 'bg-green-100 text-green-700' 
                  : 'bg-gray-900 text-white hover:bg-gray-800'
              }`}
            >
              {copied ? (
                <>
                  <FiCheck className="w-5 h-5" />
                  Copiado!
                </>
              ) : (
                <>
                  <FiCopy className="w-5 h-5" />
                  Copiar código PIX
                </>
              )}
            </button>
          </div>
        )}

        {/* Timer */}
        <div className="bg-yellow-50 rounded-xl p-4 mb-6 flex items-center gap-3">
          <FiClock className="w-5 h-5 text-yellow-600 flex-shrink-0" />
          <p className="text-sm text-yellow-800">
            O código PIX expira em <strong>30 minutos</strong>. Após o pagamento, aguarde a confirmação.
          </p>
        </div>

        {/* Verificar pagamento */}
        <button
          onClick={checkPaymentStatus}
          disabled={checkingPayment}
          className="w-full py-4 bg-green-600 text-white rounded-xl font-semibold hover:bg-green-700 transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
        >
          {checkingPayment ? (
            <>
              <FiRefreshCw className="w-5 h-5 animate-spin" />
              Verificando...
            </>
          ) : (
            <>
              <FiCheck className="w-5 h-5" />
              Já paguei, verificar pagamento
            </>
          )}
        </button>

        {/* Info */}
        <p className="text-xs text-gray-500 text-center mt-6">
          Após a confirmação do pagamento, seu pedido ficará disponível para os entregadores.
        </p>
      </main>
    </div>
  );
};

export default ExpressPaymentPage;
